import { Injectable, inject, signal } from '@angular/core'
import { CanActivateFn, ResolveEnd, Router } from '@angular/router'
import { ConfigService } from './config.service'

declare global {
  interface Window {
    dataLayer: Array<any>
    gtag?: (...args: any[]) => void
  }
}

export type CookieConsentValue = 'accepted' | 'declined' | null

const COOKIE_CONSENT_KEY = 'cookie_consent'

// Pages that show or collect health data. No Google script may be present on
// them: not loaded there, and not carried over from an earlier page either.
const SENSITIVE_PATH = /^\/(signup|dashboard)(?=[/?#]|$)/

export function isSensitivePath(path: string): boolean {
  return SENSITIVE_PATH.test(path)
}

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  consent = signal<CookieConsentValue>(null)
  private gtagLoaded = false
  private gtagInitialized = false
  private configLoaded = false

  constructor(
    private configService: ConfigService,
    private router: Router
  ) {
    this.initializeConsent()
    void this.configService.loadConfig().then(() => {
      this.configLoaded = true
      this.startGoogleTagsIfAllowed(window.location.pathname)
    })

    // A visit that started on a sensitive page gets the tags once the user
    // moves on to a regular page. ResolveEnd fires before the new page's
    // components run, so their tracking calls find gtag ready.
    this.router.events.subscribe((event) => {
      if (event instanceof ResolveEnd) {
        this.startGoogleTagsIfAllowed(event.urlAfterRedirects.split(/[?#]/)[0])
      }
    })
  }

  /** True once any Google tag code has been set up on this page. */
  hasGoogleTags(): boolean {
    return this.gtagInitialized
  }

  private startGoogleTagsIfAllowed(path: string): void {
    if (!this.configLoaded || this.gtagInitialized || isSensitivePath(path)) {
      return
    }

    this.initializeGtag()

    if (this.consent() !== null) {
      this.applyConsent(this.consent())
    }
  }

  accept(): void {
    this.setConsent('accepted')
  }

  decline(): void {
    this.setConsent('declined')
  }

  private initializeConsent(): void {
    if (typeof window === 'undefined') {
      return
    }

    const storedConsent = localStorage.getItem(COOKIE_CONSENT_KEY) as CookieConsentValue
    if (storedConsent === 'accepted' || storedConsent === 'declined') {
      this.consent.set(storedConsent)
    }
  }

  private setConsent(value: CookieConsentValue): void {
    this.consent.set(value)

    if (typeof window !== 'undefined') {
      localStorage.setItem(COOKIE_CONSENT_KEY, value ?? 'declined')
    }

    this.applyConsent(value)
  }

  private applyConsent(value: CookieConsentValue): void {
    if (value === 'accepted') {
      this.updateGoogleConsent('granted')
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({ event: 'cookie_consent_update' })
      setTimeout(() => {
        this.configureGoogleTags()
      }, 300)
    } else if (value === 'declined') {
      this.updateGoogleConsent('denied')
    }
  }

  trackContactFormSubmit(): void {
    this.trackEvent('contact_form_submit', {
      event_category: 'engagement',
      event_label: 'contact_form',
    })
    this.trackGoogleAdsConversion()
  }

  trackPhoneCall(): void {
    this.trackEvent('phone_call', {
      event_category: 'engagement',
      event_label: 'phone_call',
    })
    this.trackGoogleAdsConversion()
  }

  private trackEvent(eventName: string, params: Record<string, unknown>): void {
    if (typeof window === 'undefined' || this.isSensitiveRoute() || this.consent() !== 'accepted' || !window.gtag) {
      return
    }

    window.gtag('event', eventName, params)
  }

  private trackGoogleAdsConversion(): void {
    if (typeof window === 'undefined' || this.isSensitiveRoute() || this.consent() !== 'accepted') {
      return
    }

    const googleAdsId = this.configService.getGoogleAdsId()
    const googleAdsConversionLabel = this.configService.getGoogleAdsConversionLabel()
    // Guard against placeholder conversion labels in config
    if (!googleAdsId || !googleAdsConversionLabel || googleAdsConversionLabel === 'XXXXXXXXXXXXXXX' || !window.gtag) {
      return
    }

    window.gtag('event', 'conversion', {
      send_to: `${googleAdsId}/${googleAdsConversionLabel}`,
      value: 1.0,
      currency: 'EUR',
    })
  }

  private configureGoogleTags(): void {
    if (typeof window === 'undefined' || !window.gtag || this.isSensitiveRoute()) {
      return
    }

    const gaMeasurementId = this.configService.getGaMeasurementId()
    const googleAdsId = this.configService.getGoogleAdsId()

    if (gaMeasurementId) {
      window.gtag('config', gaMeasurementId, {
        send_page_view: true,
      })

      window.gtag('event', 'page_view', {
        send_to: gaMeasurementId,
        page_location: window.location.href,
        page_path: window.location.pathname,
        page_title: document.title,
      })
    }

    if (googleAdsId) {
      // Keep Google Ads config without page_view
      window.gtag('config', googleAdsId, { send_page_view: false })
    }
  }

  // Callers check the destination path first (startGoogleTagsIfAllowed);
  // during an in-app navigation window.location still shows the old page.
  private initializeGtag(): void {
    if (typeof window === 'undefined') {
      return
    }

    const gaMeasurementId = this.configService.getGaMeasurementId()
    const googleAdsId = this.configService.getGoogleAdsId()
    const googleTagId = this.configService.getGoogleTagId()

    if (!gaMeasurementId && !googleAdsId && !googleTagId) {
      return
    }
    this.gtagInitialized = true

    window.dataLayer = window.dataLayer || []
    window.gtag = function gtag() {
      window.dataLayer.push(arguments)
    }

    window.gtag('consent', 'default', {
      ad_storage: 'denied',
      analytics_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      functionality_storage: 'denied',
      personalization_storage: 'denied',
      security_storage: 'granted',
      wait_for_update: 500,
    })
    window.gtag('js', new Date())

    if (gaMeasurementId) {
      // Do not send page_view before consent
      window.gtag('config', gaMeasurementId, { send_page_view: false })
    }

    if (googleAdsId) {
        // Keep Google Ads config without page_view
        window.gtag('config', googleAdsId, { send_page_view: false })
    }

    // Use `googleTagId` to load `gtag.js` when available (some accounts
    // require a GT- tag to successfully fetch the library). Keep using
    // `gaMeasurementId` / `googleAdsId` as `gtag('config', ...)` targets.
    this.loadGtagScript(googleTagId || gaMeasurementId || googleAdsId)
  }

  private loadGtagScript(measurementId: string): void {
    if (this.gtagLoaded || typeof document === 'undefined') {
      return
    }

    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
    document.head.appendChild(script)
    this.gtagLoaded = true
  }

  private updateGoogleConsent(state: 'granted' | 'denied'): void {
    if (typeof window === 'undefined') {
      return
    }

    const consentUpdate = {
      ad_storage: state,
      analytics_storage: state,
      ad_user_data: state,
      ad_personalization: state,
      functionality_storage: state,
      personalization_storage: state,
      security_storage: 'granted',
    }

    window.dataLayer = window.dataLayer || []

    if (window.gtag) {
      window.gtag('consent', 'update', consentUpdate)
    }
  }

  private isSensitiveRoute(): boolean {
    if (typeof window === 'undefined') {
      return true
    }

    return isSensitivePath(window.location.pathname)
  }
}

/**
 * Route guard for sensitive pages. If Google tags are already running in this
 * page (the visitor came from a regular page), navigate with a full page load
 * instead, so the sensitive page starts in a fresh document without them.
 */
export const noGoogleTagsGuard: CanActivateFn = (_route, state) => {
  if (!inject(CookieConsentService).hasGoogleTags()) {
    return true
  }

  window.location.assign(state.url)
  return false
}
